import { HttpClient } from '@wix/http-client';
import { DsChatResponse } from '@server/types/chat';
import { ThemeUpdateRequest } from '@server/types/common';

import { getDefaultStore } from 'jotai';
import { userDsInsightsAtom, msIdAtom } from '../context';

import { extractContactInfo } from '@server/utils/regex';
import {
  setIsLoadingSuggestions,
  setStoreNameSuggestions,
  StoreNameDataAtom,
} from '../features/storeName/context/storeNameAtoms';
import { metasiteError } from '../context/errors';
import { ThemeKey } from '@common/constants/themes';
import { QuerySitesResult } from '../types/querySites';
import { colorThemeStateAtom } from '../features/templateSelection/context';
import { setQuerySitesLoading, setQuerySitesResult } from '../features/sitePreview/context/querySitesContext';

export class ChatService {
  private store;

  constructor(private httpClient: HttpClient) {
    this.store = getDefaultStore();
  }

  public async getCatalogVersion(): Promise<null> {
    try {
      const metaSiteId = this.store.get(msIdAtom);
      const response = await this.httpClient.get<any>(`/api/ds/catalog-version/${metaSiteId}`, {
        headers: { 'x-time-budget': 60000 },
      });

      return response?.data?.catalogVersion?.catalogVersion;
    } catch (err) {
      return null;
    }
  }

  public async triggerUpdateSiteSettings(): Promise<void> {
    try {
      const insights = this.store.get(userDsInsightsAtom);
      const metaSiteId = this.store.get(msIdAtom);
      if (!insights || !metaSiteId) {
        console.error('Missing insights or metaSiteId for site settings update');
        return;
      }
      await this.httpClient.post('/api/ds/update-site-settings', {
        insights,
        metaSiteId,
      });
    } catch (err) {
      console.error('Failed to update site settings:', err);
    }
  }

  async updateModalystProducts(productList: string[]): Promise<any> {
    try {
      const metaSiteId = this.store.get(msIdAtom);

      await this.httpClient.post<DsChatResponse>(
        '/api/ds/setup-modalyst-products',
        {
          productList,
          metaSiteId,
        },
        { headers: { 'x-time-budget': 40000 } },
      );
    } catch (err) {
      console.error('Failed to updateProducts:', err);
    }
  }

  public async connectPaymentProvider(): Promise<void> {
    try {
      const metaSiteId = this.store.get(msIdAtom);
      await this.httpClient.post('/api/ds/connect-provider-account', { metaSiteId });
    } catch (err) {
      console.error('Failed to connect payment provider:', err);
      throw err;
    }
  }

  public async getPaymentAggregators(): Promise<null> {
    try {
      const metaSiteId = this.store.get(msIdAtom);
      const response = await this.httpClient.get<any>(`/api/ds/payment-aggregate/${metaSiteId}`, {
        headers: { 'x-time-budget': 60000 },
      });

      return response?.data?.paymentAggregate;
    } catch (err) {
      return null;
    }
  }

  public async createSiteFromTemplate(category: string): Promise<null> {
    try {
      const response = await this.httpClient.post<any>(
        '/api/ds/create-site-from-template',
        { category },
        {
          headers: { 'x-time-budget': 60000 },
        },
      );
      const metaSiteId = response?.data?.metaSiteId?.metaSiteId;
      this.store.set(msIdAtom, metaSiteId);
    } catch (err) {
      this.store.set(metasiteError, true);
      return null;
    }
  }

  public async publishSite(): Promise<void> {
    try {
      const metaSiteId = this.store.get(msIdAtom);
      await this.httpClient.post('/api/ds/publish-site', { metaSiteId });
    } catch (err) {
      console.error('Failed to publish site:', err);
      throw err;
    }
  }

  public async updateComponent(): Promise<void> {
    try {
      const metaSiteId = this.store.get(msIdAtom);
      const { storeName, contactDetails } = this.store.get(userDsInsightsAtom);
      const { summary: storeNameSummary } = storeName;
      const { summary: contactDetailsSummary } = contactDetails;
      const { email, phone } = extractContactInfo(contactDetailsSummary ?? '');

      await this.httpClient.post('/api/ds/update-component', {
        metaSiteId,
        storeName: storeNameSummary,
        contactDetails: { email, phone },
      });
    } catch (err) {
      console.error('Failed to update component:', err);
      throw err;
    }
  }

  public async updateTheme(themeKey?: ThemeKey): Promise<void> {
    try {
      this.store.set(colorThemeStateAtom, (prev) => ({ ...prev, isLoading: true }));
      const metaSiteId = this.store.get(msIdAtom);

      const request: ThemeUpdateRequest = {
        metaSiteId,
        themeKey,
      };

      await this.httpClient.post('/api/ds/update-theme', request);
    } catch (err) {
      console.error('Failed to update theme:', err);
      throw err;
    } finally {
      this.store.set(colorThemeStateAtom, (prev) => ({ ...prev, isLoading: false }));
    }
  }

  public async suggestStoreName(category: string, products: string[]): Promise<string[]> {
    try {
      setIsLoadingSuggestions(true);
      const storeNameData = this.store.get(StoreNameDataAtom);
      const previousResults = storeNameData.suggestions;

      const response = await this.httpClient.post<{ suggestions: string[] }>(
        '/api/ds/suggest-store-name',
        {
          category,
          products,
          previousResults,
        },
        { headers: { 'x-time-budget': 20000 } },
      );
      const suggestions = response.data.suggestions;
      setStoreNameSuggestions(suggestions);
      return suggestions;
    } catch (err) {
      console.error('Failed to suggest store name:', err);
      throw err;
    } finally {
      setIsLoadingSuggestions(false);
    }
  }

  public async querySites(): Promise<QuerySitesResult | null> {
    try {
      setQuerySitesLoading(true);

      const metaSiteId = this.store.get(msIdAtom);
      if (!metaSiteId) {
        console.error('Missing metaSiteId for querySites');
        setQuerySitesResult(null);
        return null;
      }

      const response = await this.httpClient.post<QuerySitesResult>('/api/ds/query-sites', {
        metaSiteId,
      });

      setQuerySitesResult(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to query sites:', error);
      setQuerySitesResult(null);
      return null;
    } finally {
      setQuerySitesLoading(false);
    }
  }
}
